import { useEffect, useState } from "react";

type UseIsScrolledPastOptions = {
  /**
   * Scroll container to observe within. When omitted, the shared page scroll
   * container (`[data-content-scroll="true"]`) is used.
   */
  root?: HTMLElement | null;
  /**
   * Pixels to shave off the top of the root when deciding "past" — set this to
   * the height of any sticky header overlapping the top of the scroll area so
   * the target counts as scrolled past the moment it hides behind that header.
   */
  topOffset?: number;
};

/**
 * Tracks whether a target element has scrolled up and out of view (past the top
 * of the scroll container). Returns a callback ref to attach to the target so
 * the observer re-attaches correctly across mount/unmount, plus the boolean.
 */
export function useIsScrolledPast(options?: UseIsScrolledPastOptions): {
  ref: (node: HTMLElement | null) => void;
  isScrolledPast: boolean;
} {
  const root = options?.root;
  const topOffset = options?.topOffset ?? 0;
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [isScrolledPast, setIsScrolledPast] = useState(false);

  useEffect(() => {
    if (!node || typeof IntersectionObserver === "undefined") {
      setIsScrolledPast(false);
      return;
    }

    const rootEl =
      root ??
      document.querySelector<HTMLElement>('[data-content-scroll="true"]') ??
      null;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const rootTop =
          entry.rootBounds?.top ??
          rootEl?.getBoundingClientRect().top ??
          0;
        // "Past" = fully above the (offset) top edge of the scroll viewport.
        setIsScrolledPast(
          !entry.isIntersecting && entry.boundingClientRect.bottom <= rootTop
        );
      },
      {
        root: rootEl,
        rootMargin: `-${Math.max(0, Math.round(topOffset))}px 0px 0px 0px`,
        threshold: 0,
      }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [node, root, topOffset]);

  return { ref: setNode, isScrolledPast };
}

export default useIsScrolledPast;
